import { inject, Injectable, signal } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";

import { DateTime } from "luxon";

import { catchError, map, Observable, of, tap } from "rxjs";

import { PilotDataPost, PilotDataResponse, RequestConfig, RequestState, RequestStatus } from "@shared/models";
import { transformTimestampToLastUpdated } from "@shared/functions";
import { CacheId, MessageType } from "@shared/enums";
import { environment } from "@environments/environment";
import { ApiCacheService } from "../util/api-cache.service";
import { ErrorHandlingService } from "../util/error-handling.service";
import { GlobalService } from "../environment/global.service";

@Injectable({
  providedIn: "root",
})
export class PilotDataApiService {
  apiCacheService = inject(ApiCacheService);
  http = inject(HttpClient);
  errorHandlingService = inject(ErrorHandlingService);
  globalService = inject(GlobalService);

  private readonly _baseUrl = environment.imaxApiUrl;

  private _pilotDataRequestConfig: RequestConfig = {
    url: `${this._baseUrl}/GetPilotData`,
    cacheId: CacheId.PilotData,
    cacheValidMinutes: 15,
    forceRefresh: false,
  };

  timestamp = signal<string>("");
  lastUpdatedText = signal<string>("");

  getPilotData(force: boolean): Observable<RequestState<PilotDataResponse>> {
    this._pilotDataRequestConfig.forceRefresh = force;
    return this.apiCacheService
      .request<PilotDataResponse>(this._pilotDataRequestConfig)
      .pipe(tap((response) => this.handleTimestamp(response)));
  }

  updatePilotData(pilotData: PilotDataResponse, availablePVDs: number): Observable<RequestState<PilotDataResponse>> {
    this.globalService.saving.set(true);
    pilotData.availablePVDs = availablePVDs;
    const postBody: PilotDataPost = {
      pilotID: pilotData.pilotID,
      availablePVDs: availablePVDs,
    };

    const handleResponse = (): RequestState<PilotDataResponse> => {
      this.globalService.saving.set(false);
      return new RequestState<PilotDataResponse>(RequestStatus.IsCompleteWithResponse, {
        ...pilotData,
        availablePVDs,
        timestamp: new Date().toISOString(),
      });
    };

    const handleError = (error: HttpErrorResponse): Observable<RequestState<PilotDataResponse>> => {
      this.globalService.saving.set(false);
      if (error.status === 200) {
        localStorage.setItem(CacheId.PilotData, JSON.stringify(pilotData));
        this.errorHandlingService.sendMessage(MessageType.Saved);
        return of(new RequestState<PilotDataResponse>(RequestStatus.IsCompleteWithResponse, pilotData));
      }
      if (error.status === 404) {
        this.errorHandlingService.sendMessage(MessageType.UpdateRecordNotFound);
        return of(new RequestState<PilotDataResponse>(RequestStatus.HasErrorWithCachedResponse, pilotData));
      } else if (error.status === 500) {
        this.errorHandlingService.sendMessage(MessageType.ServerError);
        return of(new RequestState<PilotDataResponse>(RequestStatus.HasErrorWithCachedResponse, pilotData));
      } else {
        this.errorHandlingService.sendMessage(MessageType.UnknownError);
        return of(new RequestState<PilotDataResponse>(RequestStatus.HasErrorWithCachedResponse, pilotData));
      }
    };

    return this.http.post<string>(`${this._baseUrl}/SavePilotData`, postBody).pipe(
      map(() => handleResponse()),
      tap((response) => this.handleTimestamp(response)),
      catchError((error: HttpErrorResponse) => handleError(error))
    );
  }

  private handleTimestamp(response: RequestState<PilotDataResponse>): void {
    if (response.response) {
      const timestamp = DateTime.fromISO(response.response.timestamp);
      this.timestamp.set(response.response.timestamp);
      this.lastUpdatedText.set(transformTimestampToLastUpdated(timestamp));
    }
  }
}
